import { useEffect, useState } from 'react';
import {
  TRACKERS,
  acceptAll,
  consentIsRequired,
  declaredCategories,
  readConsent,
  refuseAll,
  writeConsent,
  type ConsentRecord,
  type TrackerDeclaration,
} from '../lib/consent.js';

/**
 * The consent banner.
 *
 * It does not appear today, and that is correct rather than broken: `TRACKERS`
 * is empty because this application stores nothing non-essential, and under
 * article 82 of the loi Informatique et Libertés a banner is then not required.
 * Showing one anyway would be asking for permission we do not need and teaching
 * people to click past the question.
 *
 * Declaring one tracker turns it on. Everything the CNIL requires is already
 * here and does not have to be remembered later:
 *
 *   * **Refuse is as prominent as accept.** Same size, same weight, side by
 *     side. A grey "Refuser" link beside a coloured "Tout accepter" button is
 *     the single most common way a banner is unlawful.
 *   * **Nothing is pre-ticked.** Each purpose starts off.
 *   * **Every purpose is named**, with who receives the data and for how long.
 *   * **Continuing to browse is not consent**: there is no dismiss-by-scrolling
 *     and no close button that means yes.
 *
 * The banner is not a cookie wall: the dashboard behind it works either way.
 */

const LABELS: Readonly<Record<string, string>> = {
  analytics: 'Mesure d’audience',
  support: 'Assistance',
  marketing: 'Marketing',
};

export interface ConsentBannerProps {
  /** Injectable so the behaviour can be tested without shipping a tracker. */
  readonly trackers?: readonly TrackerDeclaration[];
  /** Opened from the footer, to change a decision already made. */
  readonly forceOpen?: boolean;
  readonly onClose?: () => void;
  readonly onDecision?: (record: ConsentRecord) => void;
}

export function ConsentBanner({
  trackers = TRACKERS,
  forceOpen = false,
  onClose,
  onDecision,
}: ConsentBannerProps): React.JSX.Element | null {
  const [decided, setDecided] = useState<ConsentRecord | null>(null);
  const [checked, setChecked] = useState<Readonly<Record<string, boolean>>>({});
  const [detailed, setDetailed] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setDecided(readConsent());
    setReady(true);
  }, []);

  if (!consentIsRequired(trackers)) return null;
  // Reading storage happens in an effect, so the first paint knows nothing.
  // Rendering a banner then hiding it would make the page jump for somebody who
  // already answered.
  if (!ready) return null;
  if (decided !== null && !forceOpen) return null;

  const categories = declaredCategories(trackers);

  function decide(values: Readonly<Record<string, boolean>>): void {
    const record = writeConsent(values);
    setDecided(record);
    onDecision?.(record);
    onClose?.();
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Gestion des cookies"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-line bg-surface p-4 shadow-lg"
    >
      <div className="mx-auto max-w-5xl space-y-3">
        <p className="text-ui-sm text-ink">
          Nous utilisons des traceurs pour les finalités ci-dessous. Vous pouvez les accepter ou les
          refuser. Refuser n’empêche pas d’utiliser GSI Voice.
        </p>

        {detailed && (
          <ul className="space-y-2 rounded-control bg-canvas p-3">
            {trackers.map((tracker) => (
              <li key={tracker.id} className="text-ui-sm">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1"
                    // Never pre-ticked: the default is refusal.
                    checked={checked[tracker.category] === true}
                    onChange={(e) => {
                      setChecked({ ...checked, [tracker.category]: e.target.checked });
                    }}
                  />
                  <span>
                    <span className="font-medium text-ink">
                      {LABELS[tracker.category] ?? tracker.category}
                    </span>
                    <span className="block text-ink-muted">{tracker.purpose}</span>
                    <span className="block text-caption text-ink-muted">
                      Destinataire : {tracker.recipient} · Durée : {tracker.lifetime}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap gap-2">
          {/*
            Identical classes on both, deliberately. The moment one is a
            coloured button and the other a grey link, refusing is no longer as
            easy as accepting and the consent collected is not valid.
          */}
          <button
            type="button"
            onClick={() => {
              decide(refuseAll(trackers));
            }}
            className="rounded-control bg-violet px-4 py-2 text-ui-sm font-medium text-white"
          >
            Tout refuser
          </button>
          <button
            type="button"
            onClick={() => {
              decide(acceptAll(trackers));
            }}
            className="rounded-control bg-violet px-4 py-2 text-ui-sm font-medium text-white"
          >
            Tout accepter
          </button>
          {detailed ? (
            <button
              type="button"
              onClick={() => {
                decide(Object.fromEntries(categories.map((c) => [c, checked[c] === true])));
              }}
              className="rounded-control border border-line-control px-4 py-2 text-ui-sm font-medium text-ink"
            >
              Enregistrer mes choix
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setDetailed(true);
              }}
              className="rounded-control border border-line-control px-4 py-2 text-ui-sm font-medium text-ink"
            >
              Personnaliser
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
